import React, { useState } from 'react';
import { Badge, IconButton } from 'components/common';
import PropTypes from 'prop-types';
import { FiChevronDown, FiEdit2, FiTrash2, FiUser, FiClock } from 'react-icons/fi';
import createMarkup from 'helpers/createMarkup';

const FaqItem = ({ faq, editFaq, deleteFaq }) => {
  const { allow_edit, allow_delete } = faq?.permissions || {};
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`tw-transition-colors tw-duration-150 ${isOpen ? 'tw-bg-blue-50/40' : 'tw-bg-white'}`}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setIsOpen(v => !v)}
        className={`tw-w-full tw-text-left tw-px-5 tw-py-3.5 tw-flex tw-items-center tw-justify-between tw-gap-4 tw-outline-none focus:tw-outline-none tw-transition-colors tw-duration-150 tw-group/btn ${
          isOpen ? '' : 'hover:tw-bg-slate-50/70'
        }`}
      >
        {/* Chevron + Title */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
          <FiChevronDown
            size={16}
            className={`tw-flex-shrink-0 tw-transition-transform tw-duration-300 ${
              isOpen ? 'tw-rotate-180 tw-text-blue-600' : 'tw-text-slate-400 group-hover/btn:tw-text-slate-500'
            }`}
          />
          <span className={`tw-text-sm tw-font-semibold tw-leading-snug tw-transition-colors tw-duration-150 ${
            isOpen ? 'tw-text-blue-700' : 'tw-text-slate-800'
          }`}>
            {faq.title}
          </span>
        </div>

        {/* Badge + actions */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
          {faq.category_name && (
            <Badge variant="primary" size="sm">{faq.category_name}</Badge>
          )}
          {(allow_edit || allow_delete) && (
            <div className="tw-flex tw-items-center tw-gap-1 tw-opacity-0 group-hover/btn:tw-opacity-100 focus-within:tw-opacity-100 tw-transition-opacity tw-duration-150">
              {allow_edit && (
                <IconButton
                  icon={<FiEdit2 size={13} />}
                  variant="secondary"
                  size="sm"
                  onClick={e => { e.stopPropagation(); editFaq(); }}
                  title="Edit"
                />
              )}
              {allow_delete && (
                <IconButton
                  icon={<FiTrash2 size={13} />}
                  variant="danger"
                  size="sm"
                  onClick={e => { e.stopPropagation(); deleteFaq(); }}
                  title="Delete"
                />
              )}
            </div>
          )}
        </div>
      </button>

      {/* Expandable body — grid-rows height animation (no max-height clipping) */}
      <div className={`tw-grid tw-transition-all tw-duration-300 tw-ease-in-out ${
        isOpen ? 'tw-grid-rows-[1fr] tw-opacity-100' : 'tw-grid-rows-[0fr] tw-opacity-0'
      }`}>
        <div className="tw-overflow-hidden">
          <div className="tw-px-5 tw-pb-4">
            <div className="tw-ml-7 tw-border-l-2 tw-border-blue-200 tw-pl-4">
              {/* Metadata */}
              {(faq.created_by || faq.updated_date) && (
                <div className="tw-flex tw-items-center tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-mb-3 tw-pb-3 tw-border-b tw-border-slate-100">
                  {faq.created_by && (
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-400">
                      <FiUser size={11} />
                      <span>By <span className="tw-font-medium tw-text-slate-600">{faq.created_by}</span></span>
                    </div>
                  )}
                  {faq.updated_date && (
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-slate-400">
                      <FiClock size={11} />
                      <span>{faq.updated_date}</span>
                    </div>
                  )}
                </div>
              )}
              {/* Content */}
              <div
                className="tw-prose tw-prose-sm tw-max-w-none tw-text-slate-600 prose-headings:tw-font-semibold prose-headings:tw-text-slate-800 prose-a:tw-text-blue-600 prose-strong:tw-text-slate-800 prose-li:tw-text-slate-600"
                dangerouslySetInnerHTML={createMarkup(faq.description)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

FaqItem.propTypes = {
  editFaq:   PropTypes.func,
  deleteFaq: PropTypes.func,
  faq: PropTypes.shape({
    id:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    title:         PropTypes.string.isRequired,
    description:   PropTypes.string.isRequired,
    category_name: PropTypes.string,
    created_by:    PropTypes.string,
    updated_date:  PropTypes.string,
    permissions:   PropTypes.shape({
      allow_edit:   PropTypes.bool,
      allow_delete: PropTypes.bool,
    }),
  }),
};

export default FaqItem;
